import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Mic,
  Radio,
  Square,
  UserCheck,
} from 'lucide-react';
import { Button } from '../ui/Button';
import type { ApiError } from '../../lib/api';
import { useGrabadorAudio } from '../../hooks/useGrabadorAudio';
import { useWakeLock } from '../../hooks/useWakeLock';
import { borrarGrabacion } from '../../lib/grabacionEnCurso';
import { useColaMedia } from '../../lib/colaMedia';
import { sesionApi, sesionKeys, type ActividadDetalle } from '../../lib/sesion';

/**
 * MICRÓFONO DE SALA — el canal de SOPORTE del registro.
 *
 * El botón de cada quien sigue siendo el canal primario (autoría fuerte: el
 * aporte lleva la cuenta de quien lo mandó). Pero en una mesa real la gente
 * habla y se olvida de presionar. Con esto el facilitador abre una grabación
 * de toda la sala; al detenerla, el worker la procesa en batch con
 * diarización (ElevenLabs Scribe) y CADA intervención entra al acta como
 * aporte citable con su hablante y su minuto — aunque nadie haya tocado nada.
 *
 * Ponerle nombre real a «Hablante 2» es curaduría citable (ADR-0006, acción
 * `atribuir`): no edita los aportes, agrega un evento que los cita.
 */
export function MicrofonoSala({
  actividad,
  puedeConducir,
}: {
  actividad: ActividadDetalle;
  puedeConducir: boolean;
}) {
  const qc = useQueryClient();
  // Sin procesamiento del navegador: la sala entra por consola o micrófono de
  // conferencia, ya mezclada — el AGC y la supresión de ruido de Chrome
  // arruinarían justo a quien habla bajo o lejos. Bitrate alto porque son
  // grabaciones largas con muchas voces.
  const grabador = useGrabadorAudio({
    procesarVoz: false,
    bitsPorSegundo: 96_000,
    // TROZOS PERSISTIDOS: es la grabación más larga y más valiosa de todas
    // (una jornada entera). Sin esto vivía solo en la memoria de la pestaña:
    // si a la laptop se le apagaba la pantalla o el navegador la mataba, se
    // perdía completa. Ahora cada 5 s quedan a salvo en el disco del equipo.
    persistir: {
      actividadId: actividad.id,
      contexto: 'sala',
      microfonoSala: true,
      segundos: 5,
    },
  });
  // La pantalla de la Cabina no se apaga sola mientras la sala se graba.
  useWakeLock(grabador.estado === 'grabando');
  const [subido, setSubido] = useState(false);
  /** La grabación quedó guardada y se está reintentando (no se perdió). */
  const [enCola, setEnCola] = useState(false);
  // Instante en que arrancó la grabación: ancla cada intervención en tiempo
  // absoluto para poder cruzarla con los aportes del botón del mismo minuto.
  const [inicioGrabacion, setInicioGrabacion] = useState<string | null>(null);
  const [personas, setPersonas] = useState('');

  const enCurso = actividad.estado === 'abierta' || actividad.estado === 'pausada';

  const aportes = useQuery({
    queryKey: sesionKeys.aportes(actividad.id),
    queryFn: () => sesionApi.aportes(actividad.id),
    enabled: enCurso && puedeConducir,
    refetchInterval: 10_000,
  });

  /**
   * La grabación de sala pasa por la COLA DURABLE. Es la que más pesa (96 kbps
   * ≈ 43 MB/h) y por lo tanto la que más chance tiene de que se le corte la
   * subida: queda guardada en IndexedDB antes de tocar la red y se reintenta
   * sola. Perder una jornada entera de sala por un wifi malo no es una opción.
   */
  const subir = useMutation({
    mutationFn: async () => {
      const g = grabador.grabacion;
      if (!g) throw new Error('No hay grabación lista.');
      const n = Number.parseInt(personas, 10);
      const entregado = await useColaMedia.getState().encolarYEnviar({
        // El idem ES el id de la grabación: si la pestaña muriera justo ahora,
        // la recuperación reenviaría con el mismo token y el backend deduplica.
        idem: g.id,
        actividadId: actividad.id,
        tipo: 'audio',
        blob: g.blob,
        filename: `sala.${g.ext}`,
        mime: g.mime,
        inicio: inicioGrabacion ?? g.inicioISO,
        fin: g.finISO,
        duracionS: g.duracionS,
        microfonoSala: true,
        numHablantes: Number.isFinite(n) && n > 0 ? n : undefined,
      });
      // Ya vive en la cola de envío (IndexedDB): los trozos pueden liberarse.
      await borrarGrabacion(g.id);
      return entregado;
    },
    onSuccess: (entregado) => {
      setSubido(true);
      setEnCola(!entregado);
      grabador.reiniciar();
      void qc.invalidateQueries({ queryKey: ['aportes', actividad.id] });
    },
  });

  const lista = aportes.data?.aportes ?? [];
  // Intervenciones que salieron del micrófono de sala (payload.fuente).
  const intervenciones = useMemo(
    () => lista.filter((a) => a.payload?.fuente === 'sala'),
    [lista],
  );
  // Nombres ya atribuidos por curaduría: hablante → persona.
  const atribuciones = useMemo(() => {
    const mapa = new Map<string, string>();
    for (const a of lista) {
      if (a.tipo === 'conexion' && a.payload?.accion === 'atribuir') {
        const hablante = a.payload.hablante;
        const valor = a.payload.valor;
        if (typeof hablante === 'string' && typeof valor === 'string') {
          mapa.set(hablante, valor);
        }
      }
    }
    return mapa;
  }, [lista]);

  const hablantes = useMemo(() => {
    const cuenta = new Map<string, number>();
    for (const a of intervenciones) {
      const h = typeof a.payload.hablante === 'string' ? a.payload.hablante : '—';
      cuenta.set(h, (cuenta.get(h) ?? 0) + 1);
    }
    return [...cuenta.entries()].sort((a, b) => a[0].localeCompare(b[0], 'es'));
  }, [intervenciones]);

  const procesando =
    subido && !enCola && intervenciones.length === 0 && !subir.isPending;

  if (!enCurso || !puedeConducir) return null;

  const error =
    (subir.error as ApiError | null)?.detail ??
    (subir.error instanceof Error ? subir.error.message : null) ??
    grabador.error;

  return (
    <section className="rounded-md border border-border-strong bg-surface-raised px-4 py-3">
      <p className="flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-content">
        <Radio className="h-4 w-4 shrink-0 text-violet" aria-hidden="true" />
        Micrófono de sala
      </p>
      <p className="mt-1 font-sans text-xs text-content-muted">
        Red de seguridad del registro: graba la conversación completa y, al
        detenerla, cada intervención entra al acta con su hablante y su minuto
        —aunque nadie haya presionado el botón—. Avisá a la sala que se está
        grabando.
      </p>

      {grabador.estado !== 'grabando' && grabador.estado !== 'listo' && (
        <div className="mt-2 rounded border border-cyan/40 bg-surface-sunken px-3 py-2">
          <p className="font-sans text-xs font-semibold text-content">
            Antes de empezar, pedí la ronda de presentación:
          </p>
          <p className="mt-0.5 font-sans text-xs italic text-content-muted">
            «Por favor, cada persona diga su nombre y apellido y su puesto,
            uno a la vez.»
          </p>
          <p className="mt-1 font-sans text-xs text-content-muted">
            Con eso, las primeras intervenciones te dicen quién es cada voz y
            los atribuís de una vez. Ojo: las voces se reconocen dentro de{' '}
            <strong className="font-semibold">esta</strong> grabación — si
            parás y volvés a grabar, hay que atribuir de nuevo. Para una
            jornada larga conviene una sola grabación continua.
          </p>
        </div>
      )}

      {!grabador.soportado ? (
        <p className="mt-2 flex items-center gap-1.5 font-sans text-sm text-warning">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          Este navegador no permite grabar audio. Probá desde Chrome en la
          computadora de la Cabina.
        </p>
      ) : (
        <div className="mt-2 flex flex-wrap items-center gap-3">
          {grabador.estado === 'grabando' ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={grabador.detener}
                leadingIcon={<Square className="h-4 w-4" aria-hidden="true" />}
              >
                Detener y procesar
              </Button>
              <span
                className="flex items-center gap-1.5 font-sans text-sm text-error"
                aria-live="polite"
              >
                <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-error" aria-hidden="true" />
                Grabando · {formatoReloj(grabador.duracionS)}
              </span>
            </>
          ) : grabador.estado === 'listo' && grabador.grabacion ? (
            <>
              <Button
                variant="primary"
                size="sm"
                loading={subir.isPending}
                onClick={() => subir.mutate()}
                leadingIcon={<Mic className="h-4 w-4" aria-hidden="true" />}
              >
                Enviar al acta ({formatoReloj(grabador.grabacion.duracionS)})
              </Button>
              <Button variant="secondary" size="sm" onClick={grabador.reiniciar}>
                Descartar
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="secondary"
                size="sm"
                loading={grabador.estado === 'pidiendo'}
                onClick={() => {
                  setSubido(false);
                  setInicioGrabacion(new Date().toISOString());
                  void grabador.iniciar();
                }}
                leadingIcon={<Mic className="h-4 w-4" aria-hidden="true" />}
              >
                Abrir micrófono de sala
              </Button>
              <label className="flex items-center gap-1.5 font-sans text-xs text-content-muted">
                Personas en la mesa
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={32}
                  value={personas}
                  onChange={(e) => setPersonas(e.target.value)}
                  placeholder="—"
                  aria-label="Cuántas personas hay en la mesa"
                  className="min-h-touch w-16 rounded border border-border-strong bg-surface-sunken px-2 font-sans text-sm text-content focus-visible:outline-none focus-visible:shadow-focus"
                />
              </label>
            </>
          )}
        </div>
      )}

      {enCola && (
        <p className="mt-2 flex items-center gap-1.5 font-sans text-sm text-warning" role="status">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          La grabación quedó guardada en este equipo y se sigue enviando sola.
          No cierres esta pestaña hasta que confirme.
        </p>
      )}

      {procesando && (
        <p className="mt-2 flex items-center gap-1.5 font-sans text-sm text-content-muted" role="status">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
          Procesando la grabación e identificando hablantes… las intervenciones
          aparecen acá en cuanto estén.
        </p>
      )}

      {intervenciones.length > 0 && (
        <div className="mt-3 flex flex-col gap-2">
          <p className="flex items-center gap-1.5 font-sans text-sm text-success">
            <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
            {intervenciones.length} intervenciones en el acta ·{' '}
            {hablantes.length} {hablantes.length === 1 ? 'hablante' : 'hablantes'}
          </p>
          <ul className="flex flex-col gap-1.5">
            {hablantes.map(([hablante, n]) => (
              <li key={hablante}>
                <AtribuirHablante
                  actividadId={actividad.id}
                  hablante={hablante}
                  intervenciones={n}
                  nombreActual={atribuciones.get(hablante) ?? null}
                  aportesDelHablante={intervenciones
                    .filter((a) => a.payload.hablante === hablante)
                    .map((a) => a.id)}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <p className="mt-2 flex items-center gap-1.5 font-sans text-sm text-error" role="alert">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}
    </section>
  );
}

/** Ponerle nombre real a un hablante: curaduría `atribuir` (ADR-0006). */
function AtribuirHablante({
  actividadId,
  hablante,
  intervenciones,
  nombreActual,
  aportesDelHablante,
}: {
  actividadId: string;
  hablante: string;
  intervenciones: number;
  nombreActual: string | null;
  aportesDelHablante: string[];
}) {
  const qc = useQueryClient();
  const [nombre, setNombre] = useState('');

  const mut = useMutation({
    mutationFn: () =>
      sesionApi.postAporte(actividadId, {
        tipo: 'conexion',
        payload: {
          accion: 'atribuir',
          sobre: aportesDelHablante,
          valor: nombre.trim(),
          hablante,
        },
      }),
    onSuccess: () => {
      setNombre('');
      void qc.invalidateQueries({ queryKey: ['aportes', actividadId] });
    },
  });

  return (
    <div className="flex flex-wrap items-center gap-2 rounded border border-border bg-surface-sunken px-2.5 py-1.5">
      <span className="font-sans text-sm font-medium text-content">
        {hablante}
        {nombreActual && (
          <span className="ml-1.5 text-success">
            <UserCheck className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />
            {nombreActual}
          </span>
        )}
      </span>
      <span className="font-sans text-xs text-content-muted">
        {intervenciones} {intervenciones === 1 ? 'intervención' : 'intervenciones'}
      </span>
      <input
        type="text"
        value={nombre}
        onChange={(e) => setNombre(e.target.value)}
        placeholder={nombreActual ? 'Corregir el nombre…' : '¿Quién es?'}
        aria-label={`Nombre real de ${hablante}`}
        maxLength={80}
        className="min-h-touch min-w-0 flex-1 rounded border border-border-strong bg-surface-raised px-2 font-sans text-sm text-content placeholder:text-content-muted focus-visible:outline-none focus-visible:shadow-focus"
      />
      <Button
        variant="secondary"
        size="sm"
        disabled={nombre.trim().length < 2}
        loading={mut.isPending}
        onClick={() => mut.mutate()}
      >
        Atribuir
      </Button>
    </div>
  );
}

function formatoReloj(segundos: number): string {
  const m = Math.floor(segundos / 60);
  const s = Math.floor(segundos % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
